import { ApiError, type ProblemDetail } from "./client";
import type { components } from "./schema";

export type Playline = components["schemas"]["Playline"];

export type ConcurrentPlayResolution =
  | "resume"
  | "archive-and-start-new"
  | "pause-and-start-new";

export interface ConcurrentPlayDetectedProblem extends ProblemDetail {
  existingPlaylineId: string;
  existingLastPlayedAt: string | null;
  options: ConcurrentPlayResolution[];
}

export interface ResolveConcurrentPlayArgs {
  resolution: ConcurrentPlayResolution;
  existingPlaylineId: string;
  scenarioId: string;
  model?: string;
}

const CONCURRENT_PLAY_DETECTED_TYPE = "/errors/concurrent-play-detected";
const RESOLUTIONS: readonly ConcurrentPlayResolution[] = [
  "resume",
  "archive-and-start-new",
  "pause-and-start-new",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isProblemDetail(value: unknown): value is ProblemDetail {
  if (!isRecord(value)) return false;
  return (
    typeof value.type === "string" &&
    typeof value.title === "string" &&
    typeof value.status === "number" &&
    typeof value.detail === "string"
  );
}

function isConcurrentPlayResolution(
  value: unknown,
): value is ConcurrentPlayResolution {
  return (
    typeof value === "string" &&
    (RESOLUTIONS as readonly string[]).includes(value)
  );
}

function isResolutionList(
  value: unknown,
): value is ConcurrentPlayResolution[] {
  return Array.isArray(value) && value.every(isConcurrentPlayResolution);
}

function fallbackProblem(status: number, statusText: string): ProblemDetail {
  return {
    type: "about:blank",
    title: "Request failed",
    status,
    detail: statusText !== "" ? statusText : "Unknown error",
  };
}

async function parseOrThrow<T>(response: Response): Promise<T> {
  if (response.ok) {
    return (await response.json()) as T;
  }

  let problem: ProblemDetail;
  try {
    const json: unknown = await response.json();
    problem = isProblemDetail(json)
      ? json
      : fallbackProblem(response.status, response.statusText);
  } catch {
    problem = fallbackProblem(response.status, response.statusText);
  }

  throw new ApiError(response.status, problem);
}

export function getConcurrentPlayDetectedProblem(
  error: unknown,
): ConcurrentPlayDetectedProblem | null {
  if (!(error instanceof ApiError)) return null;

  const problem = error.problem;
  if (
    problem.type !== CONCURRENT_PLAY_DETECTED_TYPE ||
    problem.status !== 409 ||
    !isRecord(problem)
  ) {
    return null;
  }

  const problemRecord = problem as ProblemDetail & Record<string, unknown>;
  const existingPlaylineId = problemRecord.existingPlaylineId;
  const existingLastPlayedAt = problemRecord.existingLastPlayedAt;
  const options = problemRecord.options;

  if (typeof existingPlaylineId !== "string") return null;
  if (
    existingLastPlayedAt !== null &&
    typeof existingLastPlayedAt !== "string"
  ) {
    return null;
  }
  if (!isResolutionList(options)) return null;

  return {
    ...error.problem,
    existingPlaylineId,
    existingLastPlayedAt,
    options,
  };
}

export async function resolveConcurrentPlay(
  baseUrl: string,
  args: ResolveConcurrentPlayArgs,
): Promise<Playline> {
  const response = await fetch(`${baseUrl}/playlines/conflict-resolve`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      resolution: args.resolution,
      existingPlaylineId: args.existingPlaylineId,
      scenarioId: args.scenarioId,
      ...(args.model !== undefined ? { model: args.model } : {}),
    }),
  });

  return parseOrThrow<Playline>(response);
}
